import React, { useEffect } from 'react';
import { X } from 'react-feather';
import { BRAND_COLORS, GRAY_COLORS } from '@/UIlibrary/globalTheme';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import { Link, useParams } from 'react-router-dom';
import { Registry } from '@/graphql/__generated__/onboarding';
import CreditBureauSummary from '../Onboarding/NaturalPerson/RegistryDetails/CreditBureauSummary';
interface ProfileTextSectionProps {
	title: string;
	value: string;
	direction?: 'row' | 'col';
}

const ProfileTextSection: React.FC<ProfileTextSectionProps> = ({
	title,
	value,
	direction = 'row'
}) => {
	return (
		<div
			className={`flex flex-${direction} gap-2 ${direction === 'row' ? 'items-center' : 'items-start'}`}
		>
			<Typography size="sm" weight="semibold">
				{title}:
			</Typography>
			<Typography size="md">{value ? value : '-'}</Typography>
		</div>
	);
};

interface ProfileDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	vehiclesRequested?: string;
	clientId: string;
	fullName: string;
	t: any;
	lang: string;
	drawerWidth: number;
	onDrawerWidthChange: (width: number) => void;
	isResizing: boolean;
	onResizingChange: (isResizing: boolean) => void;
	email: string;
	phoneNumber: string;
	deliberation: string;
	registry: Registry | null | undefined;
}

const ProfileDrawer: React.FC<ProfileDrawerProps> = ({
	isOpen,
	onClose,
	vehiclesRequested,
	clientId,
	fullName,
	t,
	drawerWidth,
	onDrawerWidthChange,
	isResizing,
	onResizingChange,
	email,
	phoneNumber,
	deliberation,
	registry
}) => {
	const drawerRef = React.useRef<HTMLDivElement>(null);
	const { lang } = useParams();

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isResizing) return;

			const newWidth = window.innerWidth - e.clientX;
			if (newWidth > 200 && newWidth < 600) {
				onDrawerWidthChange(newWidth);
			}
		};

		const handleMouseUp = () => {
			onResizingChange(false);
		};

		if (isResizing) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
		}

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isResizing, onDrawerWidthChange, onResizingChange]);

	if (!isOpen) return null;

	const handleOpenWhatsapp = () => {
		const numberWithoutCountryCode = phoneNumber.replace('+52', '');
		window.open(
			`/${lang}/center/customer-support?number=${encodeURIComponent(numberWithoutCountryCode)}&name=${encodeURIComponent(fullName)}`,
			'_blank'
		);
	};

	return (
		<div
			ref={drawerRef}
			className="h-full bg-white fixed right-0 top-0 bottom-0 print:hidden"
			style={{
				width: `${drawerWidth}px`,
				height: '100vh',
				zIndex: 99
			}}
		>
			<div className="flex flex-col h-full">
				<div
					className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize group flex items-center"
					onMouseDown={() => onResizingChange(true)}
				>
					<div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-200 group-hover:bg-brand-600 transition-colors duration-200" />
					<div
						className="absolute left-0 w-4 h-8 -ml-2 top-1/2 -translate-y-1/2 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200"
						style={{ backgroundColor: 'rgba(30, 96, 145, 0.1)' }}
					>
						<div className="h-full w-full flex items-center justify-center">
							<div className="w-0.5 h-4 bg-brand-600 mx-0.5" />
							<div className="w-0.5 h-4 bg-brand-600 mx-0.5" />
						</div>
					</div>
				</div>
				<div className="flex-none p-4 border-b bg-white sticky top-0 z-10">
					<div className="flex justify-between items-center">
						<div>
							<Typography size="lg" weight="semibold">
								{t('ViewProfile')}
							</Typography>
							<Link to={`/${lang}/center/clients/?clientId=${clientId}`}>
								<Button label={t('GoToProfile')} textOnly />
							</Link>
						</div>
						<button
							onClick={onClose}
							className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
						>
							<X size={24} color={BRAND_COLORS.brand600} />
						</button>
					</div>
				</div>
				<div className="flex-1 overflow-y-auto" style={{ backgroundColor: GRAY_COLORS.gray50 }}>
					<div className="p-4 space-y-4">
						{vehiclesRequested && (
							<div
								className="flex flex-col items-center justify-center rounded-lg p-4"
								style={{ backgroundColor: 'rgba(30, 96, 145, 0.3)' }}
							>
								<Typography size="lg" weight="semibold" color={BRAND_COLORS.brand600}>
									{t('VehiclesRequested')}:{' '}
								</Typography>
								<Typography
									type="display"
									size="sm"
									weight="semibold"
									color={BRAND_COLORS.brand600}
								>
									{vehiclesRequested || t('Pending')}{' '}
								</Typography>
							</div>
						)}

						<div className="flex flex-col gap-3 px-2 py-4 bg-white rounded-lg">
							<ProfileTextSection title={t('ClientID')} value={clientId} />
							<ProfileTextSection title={t('FullName')} value={fullName} />
							<ProfileTextSection title={t('Email')} value={email} />
							<ProfileTextSection title={t('PhoneNumber')} value={phoneNumber} />
							{registry && (
								<div className="flex flex-col gap-3 px-2 py-4 bg-white rounded-lg">
									<CreditBureauSummary registry={registry} />
								</div>
							)}
							<ProfileTextSection title={t('Deliberation')} value={deliberation} direction="col" />
						</div>

						<Button label={t('GoToWhatsapp')} size="auto" onClick={handleOpenWhatsapp} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProfileDrawer;
